/**
 * Album resolution for the Bulk Upload page.
 *
 * Creation is DEFERRED to the moment Start is pressed. Creating on blur would
 * leave an empty, member-visible album behind every time an operator changed
 * their mind. And because albums.name is UNIQUE, the 23505 path is a normal
 * outcome, not an error: we re-query by exact name, select what is already
 * there, and NEVER clear the operator's file queue over it.
 */
import { describeError } from './helpers'

export const ALBUM_EXISTS_COPY = 'That album already exists — selected it for you.'

export type AlbumSelection = { kind: 'existing'; id: string } | { kind: 'new'; name: string }

export interface AlbumDeps {
  createAlbum(name: string): Promise<{ id: string | null; error: unknown | null }>
  findAlbumIdByName(name: string): Promise<{ id: string | null; error: unknown | null }>
}

export type AlbumResult =
  | { ok: true; albumId: string; created: boolean; notice?: string }
  | { ok: false; reason: string }

/** Mirrors the DB predicate: char_length(btrim(name)) between 1 and 200. */
export function validateAlbumName(raw: string): { ok: true; name: string } | { ok: false; reason: string } {
  const name = raw.trim()
  if (name.length < 1) return { ok: false, reason: 'Enter an album name.' }
  if (name.length > 200) return { ok: false, reason: 'Album names are limited to 200 characters.' }
  return { ok: true, name }
}

export async function resolveAlbumForStart(selection: AlbumSelection, deps: AlbumDeps): Promise<AlbumResult> {
  if (selection.kind === 'existing') return { ok: true, albumId: selection.id, created: false }

  const valid = validateAlbumName(selection.name)
  if (!valid.ok) return { ok: false, reason: valid.reason }

  const created = await deps.createAlbum(valid.name)
  if (!created.error && created.id) return { ok: true, albumId: created.id, created: true }

  const code = created.error && typeof created.error === 'object'
    ? String((created.error as Record<string, unknown>).code ?? '')
    : ''

  if (code === '23505') {
    const existing = await deps.findAlbumIdByName(valid.name)
    if (!existing.error && existing.id) {
      return { ok: true, albumId: existing.id, created: false, notice: ALBUM_EXISTS_COPY }
    }
    // No row AND no error is the ordinary race - another admin renamed or
    // deleted the album between the 23505 and this lookup. describeError(null)
    // would render that as "could not be looked up: unknown error", which sends
    // the operator hunting for a fault that does not exist.
    if (existing.error == null) {
      return {
        ok: false,
        reason: 'That album name was taken a moment ago but no longer exists — press Start again.',
      }
    }
    return {
      ok: false,
      reason: `That album name is taken, but it could not be looked up: ${describeError(existing.error)}`,
    }
  }

  return { ok: false, reason: describeError(created.error ?? { message: 'the album was not created' }) }
}

/** A warning, never a block: cross-batch content dedupe is deferred (photos-content-hash). */
export function existingLabelWarning(existingLabels: string[], rowLabels: string[]): string | null {
  if (existingLabels.length === 0) return null
  const known = new Set(existingLabels.map((l) => l.trim().toLowerCase()))
  const hits = rowLabels.filter((l) => known.has(l.trim().toLowerCase())).length
  if (hits === 0) return null
  const SCOPE = 'Duplicate detection applies within this batch only.'
  return hits === 1
    ? `1 photo has a label that already exists in this album. ${SCOPE}`
    : `${hits} photos have labels that already exist in this album. ${SCOPE}`
}
